import { useState, forwardRef, useEffect } from 'react';
import FormRenderer from '../FormHandler/FormRenderer';
import i18n from '../../../i18n';
import Icon from '../../../icons/Icon';
import FieldRenderer from './FieldRenderer';

const NestedForm = forwardRef(({ field, onChange, pk, values }, ref) => {
    const allowCollapse = field.collapsable !== false;
    const isOptional = Boolean(field.allowNull);
    const [isCollapsed, setIsCollapsed] = useState(true);
    // Store form data as an object: { field1: value1, field2: value2, ... } or null when cleared
    const [formData, setFormData] = useState(null);
    const [selectedOptionValue, setSelectedOptionValue] = useState(null);
    const parseFieldValue = (value) => {
        if (!value || value === 'null' || value === null) {
            return null;
        }

        // If it's already an object (not a string), return it directly
        if (typeof value === 'object' && !Array.isArray(value)) {
            return value;
        }

        // If it's a string, try to parse it (for backward compatibility)
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                // If parsed is null, return null
                if (parsed === null) {
                    return null;
                }
                // If parsed is an object (not array), return it
                if (typeof parsed === 'object' && !Array.isArray(parsed)) {
                    return parsed;
                }
            } catch (e) {
                console.warn('Failed to parse field value as JSON:', e);
                return null;
            }
        }

        return null;
    };

    const parseSelectedOptionValue = (value) => {
        if (!value || value === 'null' || value === null) {
            return null;
        }

        if (typeof value === 'object') {
            return null;
        }

        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                if (parsed !== null && typeof parsed === 'object') {
                    return null;
                }
            } catch (e) {
                return value;
            }
        }

        return value;
    };

    // Initialize form data from field.value if it exists (run when field.value changes)
    useEffect(() => {
        const parsedValue = parseFieldValue(field.value);
        const selectedValue = parseSelectedOptionValue(field.value);
        setSelectedOptionValue(selectedValue);
        if (parsedValue !== null && Object.keys(parsedValue).length > 0) {
            setFormData(parsedValue);
            setIsCollapsed(false);
        } else {
            setFormData(null);
            setIsCollapsed(allowCollapse ? (Boolean(selectedValue) || (isOptional && !field.value)) : false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value]);

    const toggleCollapse = () => {
        if (!allowCollapse) {
            return;
        }
        setIsCollapsed(!isCollapsed);
    };

    // Helper function to send form data as nested object
    const sendFormData = (data) => {
        if (onChange) {
            onChange({
                name: field.fieldName,
                value: data
            });
        }
    };

    const clearFormData = () => {
        if (!isOptional) {
            return;
        }
        setFormData(null);
        setSelectedOptionValue(null);
        sendFormData(null);
        setIsCollapsed(allowCollapse);

    };

    const localOnChange = (e) => {
        // Only handle events from nested fields - don't pass them through directly
        if (e && e.name) {
            if (e.name === field.fieldName) {
                const selectedValue = Array.isArray(e.value) ? e.value[0] : e.value;
                setSelectedOptionValue(selectedValue || null);
                setFormData(null);
                setIsCollapsed(allowCollapse ? Boolean(selectedValue) : false);
                sendFormData(selectedValue || null);
                return;
            }

            const singleValue = Array.isArray(e.value) ? e.value[0] : e.value;
            
            // Get the clean field name (remove parent field name prefix if present)
            let cleanFieldName = e.name;
            if (cleanFieldName.startsWith(`${field.fieldName}.`)) {
                cleanFieldName = cleanFieldName.substring(field.fieldName.length + 1);
            }
            
            // Calculate new data first
            const newData = formData === null ? {} : { ...formData };
            newData[cleanFieldName] = singleValue;
            
            // Update form data
            setSelectedOptionValue(null);
            setFormData(newData);
            setIsCollapsed(false);
            
            // Send updated data as nested object (only the parent object)
            // Call this after setState to avoid updating during render
            sendFormData(newData);
        }
        // Don't pass through nested field events - only send the parent object
    };
 
    const hasErrors = Boolean(field.errors && Object.keys(field.errors).length > 0);
    const hasSelectedOption = selectedOptionValue !== null && selectedOptionValue !== '';
    const hasValue = formData !== null || hasSelectedOption;

    return (
        <div className={`nested-field-form my-2 ${hasErrors ? 'has-error' : ''}`} style={{width: 'auto', flexGrow: 1}}>
            {field.options && field.options.length > 0 && (
                <div className='d-flex flex-column mb-2'>
                    <span className='col-form-label'>{i18n.t('Pick an existing') + ' ' + field.label.toLowerCase()}</span>
                   <FieldRenderer excludeLabel={true} template={field.nested_template || 'select_search'} field={field} values={values} onChange={localOnChange} pk={pk} />
                </div>
            )}
            <div
                className={`border rounded ${hasErrors ? 'border-danger' : 'border-secondary-subtle'}`}
                style={{
                    backgroundColor: hasErrors ? '#fff5f5' : 'transparent',
                }}
            >
                <div className='d-flex align-items-center justify-content-between gap-2 flex-wrap px-3 py-2'>
                    <button
                        type="button"
                        className="btn btn-link text-decoration-none p-0 flex-grow-1 text-start"
                        style={{color: 'inherit'}}
                        onClick={toggleCollapse}
                        disabled={!allowCollapse}
                    >
                        <span className='d-flex align-items-center gap-2 flex-wrap'>
                            {allowCollapse && (
                                <span
                                    className={`d-inline-flex align-items-center justify-content-center rounded-circle ${hasErrors ? 'bg-danger text-white' : 'bg-white text-primary border'}`}
                                    style={{width: '2rem', height: '2rem', flexShrink: 0}}
                                >
                                    <Icon name={isCollapsed ? 'ICON_CHEVRON_RIGHT' : 'ICON_CHEVRON_DOWN'} />
                                </span>
                            )}
                            <span className='d-flex flex-column'>
                                <span className='fw-semibold'>{field.label}</span>
                            </span>
                        </span>
                    </button>
                    <div className='d-flex align-items-center gap-2 ms-auto flex-wrap'>
                        {isOptional && (
                            <span className='badge bg-light text-muted border'>{i18n.t('Optional')}</span>
                        )}
                        {isOptional && !isCollapsed && hasValue && (
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={clearFormData}
                                title={i18n.t('Clear nested form')}
                            >
                                <Icon name="ICON_TRASH" />
                            </button>
                        )}
                    </div>
                </div>

                {!isCollapsed && !hasSelectedOption && (
                <div className="d-flex flex-wrap mx-3 mb-3  rounded bg-white p-1 position-relative" style={{borderLeft: '4px solid #0d6efd'}}>
                   
                    <FormRenderer 
                        values={values}
                        fields={(() => {
                            // Clone fields and set values from formData
                            if (!field.fields) return field.fields;
                            const fieldsWithValues = {};
                            Object.keys(field.fields).forEach(key => {
                                const nestedField = field.fields[key];
                                const nestedFieldName = nestedField.fieldName || nestedField.name;
                                // Remove parent field name prefix if present
                                const cleanFieldName = nestedFieldName.startsWith(`${field.fieldName}.`)
                                    ? nestedFieldName.substring(field.fieldName.length + 1)
                                    : nestedFieldName;

                                fieldsWithValues[key] = {
                                    ...nestedField,
                                    // Keep full dotted fieldName (e.g. category.name) so FieldRenderer's
                                    // getValueByFieldName(values, fieldName) does not read the root key "name".
                                    fieldName: nestedFieldName,
                                    value: formData !== null && formData[cleanFieldName] !== undefined ? formData[cleanFieldName] : null
                                };
                            
                                if(field.errors && field.errors[key]) {
                                    fieldsWithValues[key].errors = field.errors[key];
                                }
                            });
                            return fieldsWithValues;
                        })()}
                        ref={ref} 
                        onChange={localOnChange} 
                        pk={pk}
                    />
                </div>
                )}
            </div>
            {Array.isArray(field.errors) ?
             field.errors.map((error, index) => (
                <span className="invalid-feedback d-block" key={index}>
                    {error}
                </span>
            )):
            field.errors && Object.keys(field.errors).length > 0 &&
                Object.keys(field.errors).map((key, index) => (
                    <span className="invalid-feedback d-block" key={index}>
                        {field.errors[key].map((error, index) => (
                            <span className="invalid-feedback d-block" key={index}>
                                {error}
                            </span>
                        ))}
                    </span>
                ))
            }
        </div>
    );
});

NestedForm.displayName = 'NestedForm';

export default NestedForm;

